import React, { useEffect, useState, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setActivePage, setShowPreloader } from "../store/activePage";
import { ErrorToast, SuccessToast } from "../components/toastify";
import { AxiosGetMethod, AxiosPostMethod } from "../helpers/customAxios";
import CustomModal from "../components/modal";
import { Link } from "react-router-dom";
import HandleCreateEvent from "../hooks/handleCreateEvent";
import HandleLogout from "../hooks/handleLogout";
import QuestionsPopup from "../components/event-creations-features-questions";
import DesktopFileUpload from "../components/fileupload";
import { FaCheckCircle } from "react-icons/fa";
import convertoformdata from "../helpers/convertToFormdata";
import GetEventTypeList from "../hooks/getEventTypeList";
import PaymentComponent from "../components/payment-gateway";
import handleUpdateEventPayment from "../hooks/handleUpdatePayment";
import DateTimeSegmentInput from "../components/date-time-segment-input";
import CitySearchSelect from "../components/city-search-select";

const CREATE_EVENT_DRAFT_STORAGE_KEY = "operator-create-event-draft";

// ─── Styles ──────────────────────────────────────────────────────────────────
const inputCls =
  "w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/25 focus:border-teal-500 transition";
const selectCls =
  "w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500/25 focus:border-teal-500 transition appearance-none";

// ─── Helpers ────────────────────────────────────────────────────────────────
function FieldBox({ label, required, error, children, span }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-xs font-semibold text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
}

function SectionHeader({ icon, title, sub }) {
  return (
    <div className="mb-4 flex items-center gap-3 border-b border-slate-200 pb-3">
      <span className="text-xl">{icon}</span>
      <div>
        <p className="text-sm font-black text-slate-900">{title}</p>
        {sub && <p className="text-xs text-slate-500">{sub}</p>}
      </div>
    </div>
  );
}

function CustomerBadge({ validCus }) {
  if (validCus === null) return null;
  return (
    <div
      className={`mt-2 flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold ${validCus ? "border-teal-200 bg-teal-50 text-teal-700" : "border-red-200 bg-red-50 text-red-600"}`}
    >
      <span>{validCus ? "✓" : "✗"}</span>
      {validCus ? "Customer verified" : "Customer not found"}
    </div>
  );
}

function CustomerInfoCard({ info }) {
  if (!info) return null;
  const rows = [
    { label: "Name", value: `${info.first_name} ${info.last_name}` },
    { label: "Occupation", value: info.occupation },
    { label: "Mobile", value: info.mobile },
    { label: "Email", value: info.email },
    { label: "Address", value: info.address },
    { label: "City", value: info.city },
    { label: "State", value: info.state },
  ];
  return (
    <div className="mt-1 rounded-xl border border-teal-200 bg-teal-50/60 p-4 md:col-span-2">
      <p className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-teal-700">
        <span>✓</span> Customer Details (Read-only)
      </p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
        {rows.map(({ label, value }) => (
          <div key={label}>
            <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              {label}
            </p>
            <input
              type="text"
              disabled
              readOnly
              value={value || "—"}
              className="w-full cursor-not-allowed rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 opacity-80"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export const getTodayStart = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

export const createEventValidationSchema = (
  minimumEventDate = getTodayStart(),
) =>
  Yup.object({
    event_type: Yup.string().required("Required"),
    custom_event_type: Yup.string().when("event_type", {
      is: "Custom",
      then: (schema) => schema.trim().required("Custom event type is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    event_owner_name: Yup.string().required("Owner or family name is required"),
    eventor_1: Yup.string().trim(),
    eventor_2: Yup.string().trim(),
    event_date: Yup.date()
      .min(minimumEventDate, "Today or future date only")
      .required("Required"),
    event_address: Yup.string().required("Required"),
    event_city: Yup.string().required("Required"),
    event_state: Yup.string().required("Required"),
    event_pincode: Yup.string()
      .matches(/^\d{6}$/, "Pincode must be 6 digits")
      .required("Required"),
    customer_mobile: Yup.string()
      .required("Customer mobile is required")
      .matches(/^[0-9]{10}$/, "10 digits required"),
  });

const OperatorDashboard = () => {
  const [eventTypes, setEventTypes] = useState([]);
  const [validCus, setValidCus] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [showCusWrngModal, setShowCusWrngModal] = useState(false);
  const [pendingCustomerMobile, setPendingCustomerMobile] = useState("");
  const [pendingRegistrationDraft, setPendingRegistrationDraft] = useState(null);
  const [totalFormValues, SetTotalFormValues] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef(null);
  const formikRef = useRef(null);
  const queryParams = new URLSearchParams(location.search);
  const customerMobileFromQuery = queryParams.get("customerMobile") || "";
  const getEmptyInitialValues = () => ({
    event_type: "",
    custom_event_type: "",
    event_owner_name: "",
    eventor_1: "",
    eventor_2: "",
    event_date: "",
    event_address: "",
    event_city: "",
    event_state: "",
    event_pincode: "",
    customer_mobile: "",
    customer_id: "",
    invitation: "",
  });

  const getInitialValues = () => {
    const savedDraft = customerMobileFromQuery
      ? JSON.parse(
          sessionStorage.getItem(CREATE_EVENT_DRAFT_STORAGE_KEY) || "{}",
        )
      : {};
    return {
      event_type: savedDraft.event_type || "",
      custom_event_type: savedDraft.custom_event_type || "",
      event_owner_name: savedDraft.event_owner_name || "",
      eventor_1: savedDraft.eventor_1 || "",
      eventor_2: savedDraft.eventor_2 || "",
      event_date: savedDraft.event_date || "",
      event_address: savedDraft.event_address || "",
      event_city: savedDraft.event_city || "",
      event_state: savedDraft.event_state || "",
      event_pincode: savedDraft.event_pincode || "",
      customer_mobile: customerMobileFromQuery || "",
      customer_id: "",
      invitation: "",
    };
  };

  const [initialValues, setInitialValues] = useState(getInitialValues);

  const validationSchema = createEventValidationSchema();

  // ─── Enter Key Jump Logic ──────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const target = e.target;

      // If it's the final submit button, let it submit
      if (target.type === "submit") return;

      e.preventDefault();

      const nodes = Array.from(
        formRef.current.querySelectorAll(
          'input:not([disabled]), select:not([disabled]), button[type="submit"]',
        ),
      );

      const currentIndex = nodes.indexOf(target);
      if (currentIndex > -1 && currentIndex < nodes.length - 1) {
        nodes[currentIndex + 1].focus();
      }
    }
  };

  const handleSubmit = (values, { resetForm }) => {
    let submissionValues = { ...values };
    if (submissionValues.event_type === "Custom")
      submissionValues.event_type = submissionValues.custom_event_type;
    submissionValues.event_date =
      submissionValues.event_date.replace("T", " ") + ":00";

    if (!validCus) {
      setPendingCustomerMobile(submissionValues.customer_mobile || "");
      setPendingRegistrationDraft(submissionValues);
      setShowCusWrngModal(true);
      return;
    }
    const clearedInitialValues = getEmptyInitialValues();
    SetTotalFormValues({ ...submissionValues, eventDetailsEntered: true });
    setInitialValues(clearedInitialValues);
    resetForm({ values: clearedInitialValues });
    sessionStorage.removeItem(CREATE_EVENT_DRAFT_STORAGE_KEY);
    navigate(location.pathname, { replace: true });
    setPendingRegistrationDraft(null);
    setPendingCustomerMobile("");
    setValidCus(null);
    setCustomerInfo(null);
  };

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Event Details Form",
        activeMenu: "createEvent",
      }),
    );
    (async () => {
      const list = await GetEventTypeList();
      if (list.length > 0) setEventTypes(list.map((i) => i.event_name));
    })();
  }, [dispatch]);

  useEffect(() => {
    if (!customerMobileFromQuery) return;

    const timer = setTimeout(() => {
      checkCustomerMobile(customerMobileFromQuery, (field, value) => {
        formikRef.current?.setFieldValue(field, value);
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [customerMobileFromQuery]);

  const checkCustomerMobile = async (value, setFieldValue) => {
    dispatch(setShowPreloader(true));
    const res = await AxiosGetMethod(
      `org/get-customer-info?phone_number=${value}`,
    );
    dispatch(setShowPreloader(false));
    if (res?.status === 200) {
      setFieldValue("customer_id", res.data.data.user_id);
      setValidCus(true);
      setCustomerInfo(res.data.data);
      SuccessToast("Customer found");
    } else {
      setValidCus(false);
      setCustomerInfo(null);
      ErrorToast("Customer not found");
    }
  };

  const createEventHandler = async (formvalue, resetForm) => {
    const finalData = {
      ...totalFormValues,
      ...formvalue,
      featuresDetailsEntered: true,
      cost: process.env.REACT_APP_EVENT_PRICING,
    };
    SetTotalFormValues(finalData);
    dispatch(setShowPreloader(true));
    HandleCreateEvent(
      convertoformdata(finalData),
      dispatch,
      resetForm,
      continuetoPayment,
    );
  };

  const continuetoPayment = async (event_id) => {
    const orderDetails = await AxiosPostMethod("org/event/order-create", {
      amount: process.env.REACT_APP_EVENT_PRICING,
    });
    dispatch(setShowPreloader(false));
    if (orderDetails.status === 200) {
      PaymentComponent(
        orderDetails.data.data,
        (razval) => {
          SuccessToast("Payment successful");
          handleUpdateEventPayment(
            { ...razval, event_id, cost: process.env.REACT_APP_EVENT_PRICING },
            dispatch,
          );
        },
        () => ErrorToast("Payment failed"),
      );
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#edf5f8_0%,#f8fbfd_42%,#edf4f8_100%)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-4xl space-y-5">
        {/* Header */}
        <div className="flex items-center gap-4 rounded-3xl bg-[linear-gradient(135deg,#0f172a_0%,#16324b_48%,#0f766e_100%)] px-6 py-5 shadow-xl shadow-slate-300/60">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-teal-300/30 bg-white/10 text-2xl backdrop-blur-sm">
            🎉
          </div>
          <div>
            <h1 className="text-lg font-black tracking-wide text-white">
              Create Event
            </h1>
            <p className="mt-0.5 text-xs text-slate-200/80">
              Fill in the event details to get started
            </p>
          </div>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-xl shadow-slate-200/60 backdrop-blur">
          <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, setFieldTouched, values, errors, touched }) => (
              <Form ref={formRef} onKeyDown={handleKeyDown}>
                <div className="p-6 space-y-6">
                  {/* Event Info Section */}
                  <div>
                    <SectionHeader
                      icon="📋"
                      title="Event Information"
                      sub="Type, date and venue details"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FieldBox
                        label="Event Type"
                        required
                        error={touched.event_type && errors.event_type}
                      >
                        <div className="relative">
                          <Field
                            as="select"
                            name="event_type"
                            className={selectCls}
                            onChange={(e) => {
                              setFieldValue("event_type", e.target.value);
                              if (e.target.value !== "Custom")
                                setFieldValue("custom_event_type", "");
                            }}
                          >
                            <option value="">Select event type</option>
                            {eventTypes.map((type, i) => (
                              <option key={i} value={type}>
                                {type}
                              </option>
                            ))}
                            <option value="Custom">Custom</option>
                          </Field>
                        </div>
                        {values.event_type === "Custom" && (
                          <div className="mt-2">
                            <Field
                              type="text"
                              name="custom_event_type"
                              placeholder="Enter custom type"
                              className={inputCls}
                            />
                          </div>
                        )}
                      </FieldBox>

                      <FieldBox
                        label="Event Owner / Family Name"
                        required
                        error={touched.event_owner_name && errors.event_owner_name}
                      >
                        <Field
                          type="text"
                          name="event_owner_name"
                          placeholder="Name of the event owner or family"
                          className={inputCls}
                        />
                      </FieldBox>

                      <FieldBox
                        label="Eventor 1"
                        error={touched.eventor_1 && errors.eventor_1}
                      >
                        <Field
                          type="text"
                          name="eventor_1"
                          placeholder="Primary eventor name"
                          className={inputCls}
                        />
                      </FieldBox>

                      <FieldBox
                        label="Eventor 2"
                        error={touched.eventor_2 && errors.eventor_2}
                      >
                        <Field
                          type="text"
                          name="eventor_2"
                          placeholder="Secondary eventor name"
                          className={inputCls}
                        />
                      </FieldBox>

                      {/* Event Date — Enter cycles DD→MM→YYYY→HH→MM then moves to Address */}
                      <FieldBox
                        label="Event Date & Time"
                        required
                        error={touched.event_date && errors.event_date}
                      >
                        <DateTimeSegmentInput
                          name="event_date"
                          formikValue={values.event_date}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                        />
                      </FieldBox>

                      <FieldBox
                        label="Event Address"
                        required
                        span={2}
                        error={touched.event_address && errors.event_address}
                      >
                        <Field
                          type="text"
                          name="event_address"
                          placeholder="Full venue address"
                          className={inputCls}
                        />
                      </FieldBox>

                      <FieldBox
                        label="City"
                        required
                        error={touched.event_city && errors.event_city}
                      >
                        <CitySearchSelect
                          value={values.event_city}
                          onChange={(city) => setFieldValue("event_city", city)}
                          onBlur={() => setFieldTouched("event_city", true)}
                          className={inputCls}
                          placeholder="Search Tamil Nadu city…"
                        />
                      </FieldBox>

                      <FieldBox
                        label="State"
                        required
                        error={touched.event_state && errors.event_state}
                      >
                        <Field
                          type="text"
                          name="event_state"
                          placeholder="State"
                          className={inputCls}
                        />
                      </FieldBox>

                      <FieldBox
                        label="Pincode"
                        required
                        error={touched.event_pincode && errors.event_pincode}
                      >
                        <Field
                          type="number"
                          name="event_pincode"
                          placeholder="6-digit pincode"
                          className={inputCls}
                        />
                      </FieldBox>
                    </div>
                  </div>

                  {/* Customer Section */}
                  <div>
                    <SectionHeader
                      icon="👤"
                      title="Customer & Invitation"
                      sub="Link a customer and attach an invite image"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FieldBox
                        label="Customer Mobile"
                        required
                        error={
                          touched.customer_mobile && errors.customer_mobile
                        }
                      >
                        <Field
                          type="text"
                          name="customer_mobile"
                          maxLength={10}
                          placeholder="10-digit number"
                          className={inputCls}
                          onInput={(e) => {
                            if (e.target.value.length >= 10)
                              checkCustomerMobile(
                                e.target.value,
                                setFieldValue,
                              );
                          }}
                        />
                        <CustomerBadge validCus={validCus} />
                      </FieldBox>

                      <FieldBox
                        label="Event Invitation"
                        error={touched.invitation && errors.invitation}
                      >
                        <div className="rounded-xl border border-dashed border-teal-200 bg-teal-50/70 p-3">
                          <DesktopFileUpload
                            innerText="Upload Invitation Photo"
                            accept="image/*"
                            selectedFileURL={(file) =>
                              setFieldValue("invitation", file)
                            }
                          />
                          {values.invitation && (
                            <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-emerald-500">
                              <FaCheckCircle /> Uploaded
                            </p>
                          )}
                        </div>
                      </FieldBox>

                      <CustomerInfoCard info={customerInfo} />
                    </div>
                  </div>
                </div>

                <div className="px-6 pb-6">
                  <button
                    type="submit"
                    className="w-full py-3.5 text-white font-bold text-sm rounded-xl transition flex items-center justify-center gap-2 group hover:opacity-90 active:scale-[0.99]"
                    style={{ background: "linear-gradient(135deg, #0f766e 0%, #155e75 100%)", boxShadow: "0 10px 24px rgba(15,118,110,0.28)" }}
                  >
                    <span>Continue to Features</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 group-hover:translate-x-1 transition-transform"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <CustomModal
        type="error"
        showModal={showCusWrngModal}
        bodycontent={
          <div className="p-2 text-center">
            <p className="text-sm mb-4">
              Customer not registered. Please register{" "}
              <button
                onClick={() => {
                  sessionStorage.setItem(
                    CREATE_EVENT_DRAFT_STORAGE_KEY,
                    JSON.stringify({
                      ...pendingRegistrationDraft,
                      customer_id: "",
                      invitation: "",
                    }),
                  );
                  const registerParams = new URLSearchParams({
                    fromEventCreation: "true",
                    returnTo: "create-new-event",
                  });
                  if (pendingCustomerMobile) {
                    registerParams.set("mobile", pendingCustomerMobile);
                  }
                  navigate(`/dashboard/add-customer?${registerParams.toString()}`);
                  setShowCusWrngModal(false);
                }}
                className="text-teal-600 underline"
              >
                here
              </button>
              .
            </p>
            <button
              className="rounded-lg bg-slate-800 px-4 py-2 text-xs text-white"
              onClick={() => setShowCusWrngModal(false)}
            >
              Close
            </button>
          </div>
        }
      />
      <QuestionsPopup
        confirmSubmission={(formvalue, resetForm) =>
          createEventHandler(formvalue, resetForm)
        }
        show={
          totalFormValues?.eventDetailsEntered &&
          !totalFormValues?.featuresDetailsEntered
        }
      />
    </div>
  );
};

export default OperatorDashboard;
